import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import axios from "axios";
import { toast } from "sonner";
import {
  Home,
  FileText,
  CreditCard,
  LineChart,
  UserCircle2,
  Search,
  ScanLine,
  Plus,
  Barcode,
  CheckCircle2,
  CreditCard as CreditCardIcon,
  ChevronDown,
  Users,
  Info,
} from "lucide-react";
import AddCardSheet from "../components/AddCardSheet";
import CardDetailSheet, { getBrandGradient } from "../components/CardDetailSheet";
import BuddyLogo from '../components/BuddyLogo';
import { CARD_PALETTE, getGroupTypeConfig, getMemberAvatarColor, getMemberInitials } from "../utils/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRIMARY = "#2D5BFF";
const PRIMARY_DARK = "#1B3DBF";
const HEADER_GRADIENT = `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`;
const BUTTON_GRADIENT = `linear-gradient(180deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`;

const NAV_ITEMS = [
  { icon: Home, label: "Home", key: "home" },
  { icon: FileText, label: "Receipts", key: "receipts" },
  { icon: CreditCard, label: "Cards", key: "cards" },
  { icon: LineChart, label: "Insights", key: "insights" },
  { icon: UserCircle2, label: "Profile", key: "profile" },
];

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const getPaletteFromIndex = (idx) => {
  const safe = ((Number.isInteger(idx) ? idx : 0) % 8 + 8) % 8;
  return CARD_PALETTE[safe];
};

const buildGradient = (palette) =>
  `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)`;

// ---------- Grid card tile ----------
const GridCardTile = ({ card, onClick, sharedBy, sharedByColor }) => {
  const palette = getPaletteFromIndex(card.colour_index);
  const brandGrad = getBrandGradient(card.card_name);
  const gradient = brandGrad
    ? `linear-gradient(135deg, ${brandGrad.from} 0%, ${brandGrad.to} 100%)`
    : buildGradient(palette);
  const initials = getInitials(card.card_name);
  const last4 = card.barcode_number?.slice(-4) || "";

  return (
    <button
      onClick={onClick}
      className="relative overflow-hidden text-left"
      style={{
        aspectRatio: "3 / 2",
        borderRadius: 16,
        background: gradient,
        boxShadow: "0 8px 18px rgba(15,23,42,0.16)",
      }}
      data-testid={`grid-card-${card.id}`}
    >
      <div
        className="absolute"
        style={{
          right: -30,
          bottom: -30,
          width: 130,
          height: 130,
          borderRadius: 999,
          background: `radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%)`,
        }}
      />
      <div className="relative h-full flex flex-col justify-between" style={{ padding: 14, paddingBottom: sharedBy ? 28 : 14 }}>
        <div className="flex items-start justify-between">
          <div
            className="flex items-center justify-center"
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              backgroundColor: "rgba(255,255,255,0.22)",
              color: "#FFFFFF",
              fontSize: 10,
              fontWeight: 800,
              fontFamily: "Manrope, sans-serif",
            }}
          >
            {initials}
          </div>
          <Barcode className="w-4 h-4" style={{ color: "rgba(255,255,255,0.9)" }} />
        </div>
        <div>
          <p
            className="truncate"
            style={{
              color: "#FFFFFF",
              fontSize: 15,
              fontWeight: 800,
              fontFamily: "Manrope, sans-serif",
              letterSpacing: -0.2,
            }}
          >
            {card.card_name}
          </p>
          {last4 ? (
            <p
              style={{
                color: "rgba(255,255,255,0.85)",
                fontSize: 11,
                fontWeight: 600,
                fontFamily: "DM Sans, sans-serif",
                marginTop: 2,
                letterSpacing: 0.8,
              }}
            >
              •••• {last4}
            </p>
          ) : null}
        </div>
      </div>
      {/* Shared-by footer overlay — group view only */}
      {sharedBy && (
        <div
          className="absolute bottom-0 left-0 right-0 flex items-center gap-1.5"
          style={{
            padding: "5px 10px 6px",
            background: "linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 100%)",
          }}
        >
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{
              width: 18,
              height: 18,
              borderRadius: 999,
              backgroundColor: sharedByColor || "#7C3AED",
              color: "#FFFFFF",
              fontSize: 8,
              fontWeight: 700,
              fontFamily: "Manrope, sans-serif",
            }}
          >
            {getMemberInitials(sharedBy)}
          </div>
          <span
            style={{
              color: "rgba(255,255,255,0.92)",
              fontSize: 10,
              fontFamily: "DM Sans, sans-serif",
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {sharedBy.split(" ")[0]}
          </span>
        </div>
      )}
    </button>
  );
};

// ---------- Add card dashed tile (grid) ----------
const AddCardGridTile = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center justify-center gap-1"
    style={{
      aspectRatio: "3 / 2",
      borderRadius: 16,
      border: "2px dashed #CBD5E1",
      backgroundColor: "#F8FAFC",
      color: "#64748B",
    }}
    data-testid="add-card-grid-tile"
  >
    <div
      className="flex items-center justify-center"
      style={{
        width: 36,
        height: 36,
        borderRadius: 999,
        backgroundColor: "#EEF2FF",
      }}
    >
      <Plus className="w-5 h-5" style={{ color: PRIMARY }} />
    </div>
    <p style={{ fontSize: 13, fontWeight: 700, fontFamily: "Manrope, sans-serif", color: "#0F172A", marginTop: 6 }}>
      Add card
    </p>
    <p style={{ fontSize: 11, fontFamily: "DM Sans, sans-serif", color: "#94A3B8" }}>
      Scan or enter details
    </p>
  </button>
);

// ---------- Header (gradient) ----------
const Header = ({ search, onSearchChange, onScanClick, isGroupView, selectedGroup, groupMembers, avatarUrl, userName, onGroupPillClick }) => (
  <header
    className="relative md:rounded-none"
    style={{
      background: HEADER_GRADIENT,
      paddingTop: 18,
      paddingLeft: 20,
      paddingRight: 20,
      paddingBottom: 22,
      borderBottomLeftRadius: 28,
      borderBottomRightRadius: 28,
    }}
  >
    <div className="flex items-start justify-between">
      <div style={{ minWidth: 0, flex: 1, marginRight: 12 }}>
        <h1
          style={{
            color: "#FFFFFF",
            fontSize: 26,
            fontWeight: 800,
            fontFamily: "Manrope, sans-serif",
            letterSpacing: -0.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {isGroupView ? (selectedGroup?.name || "My Cards") : "My Cards"}
        </h1>
        {isGroupView ? (
          <div className="flex items-center gap-2 mt-2">
            <div className="flex items-center">
              {groupMembers.slice(0, 3).map((m, i) => {
                const name = m.display_name || "?";
                const initials = getMemberInitials(name);
                const bg = getMemberAvatarColor(name);
                return (
                  <div key={i} className="flex items-center justify-center overflow-hidden flex-shrink-0" style={{
                    width: 28, height: 28, borderRadius: 999, backgroundColor: bg,
                    border: "2px solid rgba(45,91,255,0.8)", marginLeft: i > 0 ? -8 : 0,
                    zIndex: groupMembers.length - i, position: "relative",
                    color: "#FFFFFF", fontSize: 10, fontWeight: 700, fontFamily: "Manrope, sans-serif",
                  }}>
                    {initials}
                  </div>
                );
              })}
            </div>
            <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 13, fontFamily: "DM Sans, sans-serif" }}>
              {selectedGroup.member_count || groupMembers.length} member{(selectedGroup.member_count || groupMembers.length) !== 1 ? "s" : ""} sharing
            </span>
          </div>
        ) : (
          <p style={{ color: "rgba(255,255,255,0.78)", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginTop: 2 }}>
            Scan, store and show at checkout
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        <GroupSwitcherPill
          selectedGroup={selectedGroup}
          avatarUrl={avatarUrl}
          userName={userName}
          onClick={onGroupPillClick}
        />
        <button
          onClick={onScanClick}
          className="flex items-center gap-1.5"
          style={{
            backgroundColor: "rgba(255,255,255,0.18)",
            color: "#FFFFFF",
            padding: "8px 12px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            fontFamily: "DM Sans, sans-serif",
          }}
          data-testid="header-scan-btn"
        >
          <ScanLine className="w-4 h-4" />
          Scan
        </button>
      </div>
    </div>

    <div className="relative" style={{ marginTop: 18 }}>
      <Search
        className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4"
        style={{ color: "#94A3B8" }}
      />
      <input
        type="text"
        placeholder="Search cards..."
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        className="w-full"
        style={{
          height: 44,
          paddingLeft: 38,
          paddingRight: 14,
          borderRadius: 14,
          backgroundColor: "#FFFFFF",
          border: "none",
          outline: "none",
          fontSize: 16,
          fontFamily: "DM Sans, sans-serif",
          color: "#0F172A",
          boxShadow: "0 6px 16px rgba(15,23,42,0.08)",
        }}
        data-testid="cards-search-input"
      />
    </div>
  </header>
);

// ---------- Empty state body ----------

const BUDDY_CARDS_IMG = "data:image/webp;base64,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";
const EmptyState = ({ onAdd }) => (
  <div
    style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "24px",
      minHeight: "calc(100dvh - 180px)",
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: 400,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
      className="md:bg-white md:rounded-3xl md:shadow-sm md:border md:border-slate-100 md:p-16"
    >
      <img
        src={BUDDY_CARDS_IMG}
        alt="My Buddy"
        style={{ width: 140, height: 140, objectFit: "contain" }}
        data-testid="cards-empty-illustration"
      />
      <h2
        style={{
          color: "#0F172A",
          fontSize: 22,
          fontWeight: 800,
          fontFamily: "Manrope, sans-serif",
          marginTop: 18,
          letterSpacing: -0.4,
        }}
      >
        No cards yet
      </h2>
      <p
        style={{
          color: "#64748B",
          fontSize: 14,
          fontFamily: "DM Sans, sans-serif",
          lineHeight: 1.5,
          marginTop: 8,
          maxWidth: 280,
        }}
      >
        Add your loyalty cards and I'll keep them all safe in one place.
      </p>
      <button
        onClick={onAdd}
        className="flex items-center gap-2 justify-center"
        style={{
          marginTop: 24,
          background: BUTTON_GRADIENT,
          color: "#FFFFFF",
          height: 52,
          paddingLeft: 24,
          paddingRight: 24,
          borderRadius: 999,
          fontSize: 15,
          fontWeight: 800,
          fontFamily: "Manrope, sans-serif",
          boxShadow: "0 10px 22px rgba(45,91,255,0.32)",
          minWidth: 220,
        }}
        data-testid="add-first-card-btn"
      >
        <CreditCardIcon className="w-5 h-5" />
        Add first card
      </button>
      <div
        className="flex items-center gap-1.5"
        style={{
          marginTop: 22,
          backgroundColor: "#F1F5F9",
          padding: "8px 14px",
          borderRadius: 999,
        }}
        data-testid="cards-integration-hint"
      >
        <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#94A3B8" }} />
        <span style={{ color: "#64748B", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>
          Tesco · Nectar · Boots · Clubcard &amp; more
        </span>
      </div>
    </div>
  </div>
);

// ---------- Group switcher pill ----------
const GroupSwitcherPill = ({ selectedGroup, avatarUrl, userName, onClick }) => {
  if (selectedGroup) {
    const cfg = getGroupTypeConfig(selectedGroup.type);
    const Icon = cfg.icon;
    return (
      <button onClick={onClick} className="flex items-center gap-2" style={{ backgroundColor: "rgba(255,255,255,0.15)", borderRadius: 999, padding: "6px 14px 6px 8px", flexShrink: 0 }}>
        <div className="flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 999, backgroundColor: cfg.bg, fontSize: 15 }}>
          {selectedGroup.emoji
            ? <span style={{ lineHeight: 1 }}>{selectedGroup.emoji}</span>
            : <Icon className="w-3.5 h-3.5" style={{ color: "#FFFFFF" }} />}
        </div>
        <span className="truncate" style={{ color: "#FFFFFF", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif", maxWidth: 100 }}>{selectedGroup.name}</span>
        <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: "rgba(255,255,255,0.7)" }} />
      </button>
    );
  }
  const initial = (userName || "U").charAt(0).toUpperCase();
  return (
    <button onClick={onClick} className="flex items-center gap-2" style={{ backgroundColor: "rgba(255,255,255,0.15)", borderRadius: 999, padding: "6px 14px 6px 8px", flexShrink: 0 }}>
      <div className="flex items-center justify-center overflow-hidden" style={{ width: 28, height: 28, borderRadius: 999, backgroundColor: avatarUrl ? "transparent" : "rgba(255,255,255,0.2)", color: "#FFFFFF", fontWeight: 700, fontSize: 11, fontFamily: "Manrope, sans-serif" }}>
        {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : initial}
      </div>
      <span style={{ color: "#FFFFFF", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>Personal</span>
      <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: "rgba(255,255,255,0.7)" }} />
    </button>
  );
};

// ---------- Card group switcher sheet (loyalty_cards groups only) ----------
const CardGroupSwitcherSheet = ({ isOpen, onClose, groups, selectedGroupId, onSelect, onCreateGroup, onManageGroups, userName, avatarUrl }) => {
  if (!isOpen) return null;
  const eligibleGroups = groups.filter(g => g.sharing?.loyalty_cards === true);
  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-50" style={{ backgroundColor: "rgba(0,0,0,0.4)" }} />
      <div className="fixed left-0 right-0 bottom-0 z-50" style={{ backgroundColor: "#FFFFFF", borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: "12px 20px 32px", maxHeight: "70vh", overflowY: "auto", boxShadow: "0 -8px 32px rgba(0,0,0,0.15)" }}>
        <div className="flex justify-center mb-4"><div style={{ width: 36, height: 4, borderRadius: 999, backgroundColor: "#D1D5DB" }} /></div>
        <p style={{ color: "#6B7280", fontSize: 12, fontWeight: 700, fontFamily: "DM Sans, sans-serif", letterSpacing: 1, marginBottom: 12 }}>SWITCH TO</p>
        {/* Info note */}
        <div className="flex items-center gap-2 mb-3" style={{ padding: "8px 12px", backgroundColor: "#EEF2FF", borderRadius: 10 }}>
          <Info className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#4338CA" }} />
          <p style={{ color: "#4338CA", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>Cards can only be shared in Household circles</p>
        </div>
        {/* Personal row */}
        <button onClick={() => onSelect(null)} className="w-full flex items-center gap-3 py-3" style={{ borderBottom: "1px solid #F3F4F6" }}>
          <div className="flex items-center justify-center overflow-hidden flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: avatarUrl ? "transparent" : PRIMARY, color: "#FFFFFF", fontWeight: 700, fontSize: 14, fontFamily: "Manrope, sans-serif" }}>
            {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : (userName || "U").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>Personal</p>
            <p style={{ color: "#9CA3AF", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginTop: 1 }}>Just your cards</p>
          </div>
          {!selectedGroupId && <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: PRIMARY }} />}
        </button>
        {/* Eligible groups */}
        {eligibleGroups.length === 0 ? (
          <p style={{ color: "#9CA3AF", fontSize: 13, fontFamily: "DM Sans, sans-serif", padding: "16px 0" }}>
            No Household circles found. Create one to share cards.
          </p>
        ) : (
          eligibleGroups.map(g => {
            const cfg = getGroupTypeConfig(g.type);
            const Icon = cfg.icon;
            return (
              <button key={g.id} onClick={() => onSelect(g)} className="w-full flex items-center gap-3 py-3" style={{ borderBottom: "1px solid #F3F4F6" }}>
                <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: cfg.bg, fontSize: 20 }}>
                  {g.emoji ? <span style={{ lineHeight: 1 }}>{g.emoji}</span> : <Icon className="w-5 h-5" style={{ color: "#FFFFFF" }} />}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>{g.name}</p>
                  <p style={{ color: "#9CA3AF", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginTop: 1 }}>{cfg.label} · {g.member_count} member{g.member_count !== 1 ? "s" : ""}</p>
                </div>
                {selectedGroupId === g.id && <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: PRIMARY }} />}
              </button>
            );
          })
        )}
        <div style={{ marginTop: 8 }}>
          <button onClick={onCreateGroup} className="w-full flex items-center gap-3 py-3">
            <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: "#F3F4F6" }}><Plus className="w-5 h-5" style={{ color: PRIMARY }} /></div>
            <p style={{ color: PRIMARY, fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>Create a circle</p>
          </button>
          <button onClick={onManageGroups} className="w-full flex items-center gap-3 py-3">
            <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: "#F3F4F6" }}><Users className="w-5 h-5" style={{ color: "#6B7280" }} /></div>
            <p style={{ color: "#6B7280", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>Manage circles</p>
          </button>
        </div>
      </div>
    </>
  );
};

// ---------- Group empty state (no cards shared in this group yet) ----------
const GroupEmptyCards = ({ groupName }) => (
  <div className="flex items-start gap-3" style={{ margin: "24px 20px", padding: 16, backgroundColor: "#EEF2FF", borderRadius: 16, border: "1px solid #C7D2FE" }}>
    <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#4338CA" }} />
    <div>
      <p style={{ color: "#3730A3", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>No cards shared yet</p>
      <p style={{ color: "#4338CA", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginTop: 3, lineHeight: 1.5 }}>
        Open a card and tap <strong>Circle Sharing</strong> to share it in <strong>{groupName}</strong>.
      </p>
    </div>
  </div>
);

// ---------- Skeleton ----------
const Skeleton = () => (
  <div className="w-full md:max-w-5xl md:mx-auto space-y-4 px-5 pt-4">
    <div className="md:grid md:grid-cols-2 md:gap-4 space-y-3 md:space-y-0">
      <div className="h-32 rounded-2xl" style={{ backgroundColor: "#F1F5F9" }} />
      <div className="h-32 rounded-2xl" style={{ backgroundColor: "#F1F5F9" }} />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
      <div className="rounded-2xl" style={{ aspectRatio: "1.05 / 1", backgroundColor: "#F1F5F9" }} />
      <div className="rounded-2xl" style={{ aspectRatio: "1.05 / 1", backgroundColor: "#F1F5F9" }} />
      <div className="rounded-2xl hidden md:block" style={{ aspectRatio: "1.05 / 1", backgroundColor: "#F1F5F9" }} />
      <div className="rounded-2xl hidden lg:block" style={{ aspectRatio: "1.05 / 1", backgroundColor: "#F1F5F9" }} />
    </div>
  </div>
);

// ---------- Bottom Navigation ----------
const BottomNav = ({ activeKey, onNav }) => (
  <nav
    className="md:hidden fixed bottom-0 left-0 right-0 flex justify-around items-center"
    style={{
      backgroundColor: "#FFFFFF",
      borderTop: "1px solid #F3F4F6",
      paddingTop: 10,
      paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
      zIndex: 50,
    }}
    data-testid="bottom-nav"
  >
    {NAV_ITEMS.map((item) => {
      const active = activeKey === item.key;
      return (
        <button
          key={item.key}
          onClick={() => onNav(item.key)}
          className="flex flex-col items-center gap-1"
          data-testid={`bottom-nav-${item.key}`}
        >
          <item.icon
            className="w-5 h-5"
            style={{ color: active ? PRIMARY : "#9CA3AF" }}
            fill={active ? PRIMARY : "none"}
          />
          <span
            style={{
              color: active ? PRIMARY : "#9CA3AF",
              fontSize: 11,
              fontFamily: "DM Sans, sans-serif",
              fontWeight: active ? 600 : 500,
            }}
          >
            {item.label}
          </span>
        </button>
      );
    })}
  </nav>
);

// ============================== Main ==============================

export default function CardsPage() {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedCardMeta, setSelectedCardMeta] = useState({ isOwner: true, sharedByName: null, sharingInfo: [] });
  const [search, setSearch] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  // Group switcher state
  const [showGroupSwitcher, setShowGroupSwitcher] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupCards, setGroupCards] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const isGroupView = !!selectedGroup;

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const fetchCards = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cards`, { withCredentials: true });
      setCards(res.data.cards || []);
    } catch (err) {
      console.error("Cards fetch error:", err);
      if (err.response?.status === 401) {
        navigate("/login", { replace: true });
      } else {
        toast.error("Couldn't load cards");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  // Fetch groups + restore sessionStorage selection (Cards: only restore if loyalty_cards === true)
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(`${API}/groups`, { withCredentials: true });
        const fetched = res.data.groups || [];
        setGroups(fetched);
        try {
          const saved = sessionStorage.getItem("mb_selected_group");
          if (saved) {
            const parsed = JSON.parse(saved);
            const match = fetched.find(g => g.id === parsed.id);
            // Only restore if this group supports card sharing — else silently fall back to Personal
            if (match && match.sharing?.loyalty_cards === true) {
              setSelectedGroup(match);
            }
          }
        } catch { /* ignore */ }
      } catch { /* silent */ }
    };
    fetchGroups();
  }, []);

  // Fetch group cards + members when a group is selected
  useEffect(() => {
    if (!selectedGroup) { setGroupCards([]); setGroupMembers([]); return; }
    const fetchGroupData = async () => {
      setGroupLoading(true);
      try {
        const [itemsRes, summaryRes] = await Promise.all([
          axios.get(`${API}/groups/${selectedGroup.id}/items?type=card`, { withCredentials: true }),
          axios.get(`${API}/groups/${selectedGroup.id}/summary`, { withCredentials: true }),
        ]);
        setGroupCards(itemsRes.data.items || []);
        setGroupMembers(summaryRes.data.members || []);
      } catch { }
      finally { setGroupLoading(false); }
    };
    fetchGroupData();
  }, [selectedGroup]);

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
    setShowGroupSwitcher(false);
    setSearch("");
    // Persist to sessionStorage so Receipts/Dashboard restore their own selected group
    try {
      if (group) sessionStorage.setItem("mb_selected_group", JSON.stringify({ id: group.id }));
      else sessionStorage.removeItem("mb_selected_group");
    } catch { /* ignore */ }
  };

  const filteredCards = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) => (c.card_name || "").toLowerCase().includes(q));
  }, [cards, search]);

  const sortedCards = useMemo(() => {
    return [...filteredCards].sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const dbb = new Date(b.created_at || 0).getTime();
      return dbb - da;
    });
  }, [filteredCards]);

  const onNavClick = (key) => {
    if (key === "cards") return;
    if (key === "home") { navigate("/dashboard"); return; }
    if (key === "receipts") { navigate("/receipts"); return; }
    if (key === "insights") { navigate("/insights"); return; }
    if (key === "profile") { navigate("/profile"); return; }
  };

  // Open card detail — personal view always owner; group view derives ownership from shared_by
  const handleCardClick = (card, groupCardMeta = null) => {
    if (groupCardMeta) {
      const isOwner = groupCardMeta.shared_by === authUser?.id;
      setSelectedCardMeta({
        isOwner,
        sharedByName: isOwner ? null : groupCardMeta.shared_by_name,
        sharingInfo: [{
          share_id: groupCardMeta.share_id,
          group_id: selectedGroup.id,
          group_name: selectedGroup.name,
          group_type: selectedGroup.type,
          emoji: selectedGroup.emoji,
        }],
      });
    } else {
      setSelectedCardMeta({ isOwner: true, sharedByName: null, sharingInfo: [] });
    }
    setSelectedCard(card);
  };

  const handleCardDeleted = (deletedId) => {
    setCards((prev) => prev.filter((c) => c.id !== deletedId));
  };

  const handleCardUpdated = (updatedCard) => {
    setCards((prev) => prev.map((c) => (c.id === updatedCard.id ? updatedCard : c)));
  };

  const isEmpty = !loading && cards.length === 0 && !isGroupView;

  const userName = authUser?.first_name || "User";
  const lastName = authUser?.last_name || "";
  const userEmail = authUser?.email || "";
  const avatarUrl = authUser?.avatar_url;

  // Shared modals
  const modals = (
    <>
      <AddCardSheet
        isOpen={showAddSheet}
        onClose={() => setShowAddSheet(false)}
        onSaved={fetchCards}
      />
      <CardDetailSheet
        card={selectedCard}
        isOpen={!!selectedCard}
        onClose={() => setSelectedCard(null)}
        onDeleted={handleCardDeleted}
        onUpdated={handleCardUpdated}
        isOwner={selectedCardMeta.isOwner}
        sharedByName={selectedCardMeta.sharedByName}
        sharingInfo={selectedCardMeta.sharingInfo}
      />
      <CardGroupSwitcherSheet
        isOpen={showGroupSwitcher}
        onClose={() => setShowGroupSwitcher(false)}
        groups={groups}
        selectedGroupId={selectedGroup?.id}
        onSelect={handleGroupSelect}
        onCreateGroup={() => { setShowGroupSwitcher(false); navigate("/groups/create"); }}
        onManageGroups={() => { setShowGroupSwitcher(false); navigate("/profile"); }}
        userName={userName}
        avatarUrl={avatarUrl}
      />
    </>
  );

  // Shared card grid content
  const cardContent = (padding) => {
    if (isGroupView && groupLoading) {
      return <Skeleton />;
    }
    if (isGroupView && groupCards.length === 0) {
      return <GroupEmptyCards groupName={selectedGroup.name} />;
    }
    return (
      <div style={{ padding }}>
        <section className="space-y-3" data-testid="all-cards-section">
          {!isGroupView && (
            <h3 style={{ color: "#94A3B8", fontSize: 11, fontWeight: 800, letterSpacing: 1.6, fontFamily: "DM Sans, sans-serif" }}>
              ALL CARDS
            </h3>
          )}
          <div className={`grid gap-3 ${isMobile ? "grid-cols-2" : "grid-cols-4 gap-4"}`}>
            {isGroupView ? (
              groupCards.map(gCard => (
                <GridCardTile
                  key={gCard.share_id}
                  card={gCard.item}
                  sharedBy={gCard.shared_by_name}
                  sharedByColor={getMemberAvatarColor(gCard.shared_by_name)}
                  onClick={() => handleCardClick(gCard.item, gCard)}
                />
              ))
            ) : (
              <>
                {sortedCards.map(card => (
                  <GridCardTile
                    key={`grid-${card.id}`}
                    card={card}
                    onClick={() => handleCardClick(card)}
                  />
                ))}
                <AddCardGridTile onClick={() => setShowAddSheet(true)} />
              </>
            )}
          </div>
        </section>
      </div>
    );
  };

  // ------------ MOBILE ------------
  if (isMobile) {
    return (
      <div
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: "#FFFFFF", paddingBottom: "calc(80px + env(safe-area-inset-bottom, 34px))" }}
        data-testid="cards-page"
      >
        <Header
          search={search}
          onSearchChange={setSearch}
          onScanClick={() => setShowAddSheet(true)}
          isGroupView={isGroupView}
          selectedGroup={selectedGroup}
          groupMembers={groupMembers}
          avatarUrl={avatarUrl}
          userName={userName}
          onGroupPillClick={() => setShowGroupSwitcher(true)}
        />

        {loading ? (
          <Skeleton />
        ) : isEmpty ? (
          <EmptyState onAdd={() => setShowAddSheet(true)} />
        ) : (
          <main className="flex-1">
            {cardContent("20px 20px 22px")}
          </main>
        )}

        <BottomNav activeKey="cards" onNav={onNavClick} />
        {modals}
      </div>
    );
  }

  // ------------ DESKTOP ------------
  return (
    <div
      className="h-screen flex overflow-hidden"
      style={{ backgroundColor: "#F3F4F6" }}
      data-testid="cards-page"
    >
      {/* Sidebar */}
      <aside
        className="flex flex-col"
        style={{ width: 240, backgroundColor: "#F9FAFB", borderRight: "1px solid #F3F4F6" }}
      >
        <div className="flex items-center gap-2 px-5 pt-6 pb-8">
          <BuddyLogo size={28} />
          <span
            style={{
              color: "#111827",
              fontSize: 18,
              fontWeight: 600,
              fontFamily: "Manrope, sans-serif",
            }}
          >
            My Buddy
          </span>
        </div>

        <nav className="flex-1 px-3">
          {NAV_ITEMS.map((item) => {
            const active = item.key === "cards";
            return (
              <button
                key={item.key}
                onClick={() => onNavClick(item.key)}
                className="w-full flex items-center gap-3 px-4 py-3 mb-1 transition-all"
                style={{
                  backgroundColor: active ? "#E0E7FF" : "transparent",
                  color: active ? PRIMARY : "#6B7280",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: active ? 600 : 500,
                  fontFamily: "DM Sans, sans-serif",
                }}
                data-testid={`sidebar-nav-${item.key}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t" style={{ borderColor: "#F3F4F6" }}>
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center overflow-hidden flex-shrink-0"
              style={{
                width: 40,
                height: 40,
                borderRadius: 999,
                backgroundColor: avatarUrl ? "transparent" : PRIMARY,
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: 14,
                fontFamily: "Manrope, sans-serif",
              }}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt={userName} className="w-full h-full object-cover" />
              ) : (
                (userName || "U").charAt(0).toUpperCase()
              )}
            </div>
            <div className="min-w-0">
              <p
                className="truncate"
                style={{ color: "#111827", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}
              >
                {userName} {lastName}
              </p>
              {userEmail ? (
                <p
                  className="truncate"
                  style={{ color: "#6B7280", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}
                >
                  {userEmail}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {/* Header bar — blue gradient */}
        <div style={{ background: HEADER_GRADIENT, padding: "28px 40px 24px" }}>
          <div className="flex items-start justify-between mb-4">
            <div style={{ minWidth: 0, flex: 1, marginRight: 12 }}>
              <h1
                style={{
                  color: "#FFFFFF",
                  fontSize: 26,
                  fontWeight: 800,
                  fontFamily: "Manrope, sans-serif",
                  letterSpacing: -0.5,
                  lineHeight: 1.2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {isGroupView ? (selectedGroup?.name || "My Cards") : "My Cards"}
              </h1>
              {isGroupView ? (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex items-center">
                    {groupMembers.slice(0, 4).map((m, i) => {
                      const name = m.display_name || "?";
                      const initials = getMemberInitials(name);
                      const bg = getMemberAvatarColor(name);
                      return (
                        <div key={i} className="flex items-center justify-center overflow-hidden flex-shrink-0" style={{
                          width: 28, height: 28, borderRadius: 999, backgroundColor: bg,
                          border: "2px solid rgba(45,91,255,0.8)", marginLeft: i > 0 ? -8 : 0,
                          zIndex: groupMembers.length - i, position: "relative",
                          color: "#FFFFFF", fontSize: 10, fontWeight: 700, fontFamily: "Manrope, sans-serif",
                        }}>
                          {initials}
                        </div>
                      );
                    })}
                  </div>
                  <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 13, fontFamily: "DM Sans, sans-serif" }}>
                    {selectedGroup.member_count || groupMembers.length} member{(selectedGroup.member_count || groupMembers.length) !== 1 ? "s" : ""} sharing
                  </span>
                </div>
              ) : (
                <p style={{ color: "rgba(255,255,255,0.78)", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginTop: 2 }}>
                  Scan, store &amp; use loyalty cards in one place
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <GroupSwitcherPill
                selectedGroup={selectedGroup}
                avatarUrl={avatarUrl}
                userName={userName}
                onClick={() => setShowGroupSwitcher(true)}
              />
              {!isGroupView && (
                <button
                  onClick={() => setShowAddSheet(true)}
                  className="flex items-center gap-1.5"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.18)",
                    color: "#FFFFFF",
                    padding: "8px 16px",
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 700,
                    fontFamily: "DM Sans, sans-serif",
                  }}
                  data-testid="desktop-add-card-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add card
                </button>
              )}
            </div>
          </div>
          {/* White search bar — personal view only */}
          {!isGroupView && (
            <div className="relative">
              <Search
                className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: "#94A3B8" }}
              />
              <input
                type="text"
                placeholder="Search cards..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full"
                style={{
                  height: 44,
                  paddingLeft: 38,
                  paddingRight: 14,
                  borderRadius: 14,
                  backgroundColor: "#FFFFFF",
                  border: "none",
                  outline: "none",
                  fontSize: 16,
                  fontFamily: "DM Sans, sans-serif",
                  color: "#0F172A",
                  boxShadow: "0 6px 16px rgba(15,23,42,0.08)",
                }}
                data-testid="cards-search-input"
              />
            </div>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <Skeleton />
        ) : isEmpty ? (
          <div className="flex items-center justify-center" style={{ minHeight: "calc(100vh - 80px)" }}>
            <EmptyState onAdd={() => setShowAddSheet(true)} />
          </div>
        ) : (
          cardContent("30px 40px")
        )}
      </main>

      {modals}
    </div>
  );
}